import { ReactNode, useState } from "react";

import { useTranslator } from "@eo-locale/react";
import isEmpty from "lodash/isEmpty";

import { Box, useDisclosure } from "@/storybook-components";

import {
  Menu,
  AlertDialog,
  FiMoreVertical,
  type AlertDialogProps
} from "@/storybook-components";
import { useAppSelector } from "@/redux/store/hooks";

// Alert config carried by a menu item. Handlers receive the row's data (idea,
// comment, datum, ...) so the param stays a passthrough `any`; the value of this
// type is the known alert keys, which catch config typos at the call site.
type RowDataHandler = (currentData?: any) => void;
type AlertContent = Omit<
  AlertDialogProps,
  "open" | "onClose" | "onPrimaryClickHandler" | "onSecondaryClickHandler"
> & {
  onPrimaryClickHandler?: RowDataHandler;
  onSecondaryClickHandler?: RowDataHandler;
};

export interface MenuItemConfig {
  icon?: ReactNode;
  title?: string;
  type?: string;
  isItemVisible?: boolean;
  actionHandler?: RowDataHandler | null;
  hasAlert?: boolean;
  loggedInUser?: string;
  menuItemColor?: string;
  menuListLabel?: string;
  alertContent?: AlertContent | null;
  disabled?: boolean;
}

type CurrentAlertContent = AlertContent & { currentData?: unknown };

interface ActionListProps {
  menuList?: MenuItemConfig[];
  currentData?: {
    author?: string;
    userType?: string;
    [key: string]: unknown;
  } | null;
}

const ActionList = ({
  menuList,
  currentData
}: ActionListProps): JSX.Element => {
  const { onClose } = useDisclosure();
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [currentAlertContent, setCurrentAlertContent] =
    useState<CurrentAlertContent | null>(null);
  const { translate } = useTranslator();
  const { type: loggedInUserType } = useAppSelector(
    (state) => state.user?.registeredUser?.profile
  );

  // to open and close alert dialog based on menu item click
  const openAlert = (alertContent?: AlertContent | null) => {
    if (!alertContent) return;
    onClose();
    setAlertOpen(true);
    setCurrentAlertContent({ ...alertContent, currentData });
  };

  const closeAlert = () => {
    setAlertOpen(false);
  };

  const menuItemClickHandler = (item: MenuItemConfig) => {
    if (item.hasAlert) openAlert(item.alertContent);
    else item.actionHandler?.(currentData);
  };

  // iterate through each menu item
  if (!menuList || menuList.length === 0) {
    return null;
  }
  const menuItems = menuList.map((menuItem, index) => {
    if (!menuItem.isItemVisible) return null;

    const isUserSpecific =
      menuItem.type === "edit" || menuItem.type === "delete";
    const isLoggedInUser = currentData?.author === menuItem.loggedInUser;
    const isMatchingUserType = currentData?.userType === loggedInUserType;
    // loggedInUser is the `login` name of signed user

    if (
      (isUserSpecific && isLoggedInUser && isMatchingUserType) ||
      !isUserSpecific
    ) {
      return (
        <Menu.Item
          key={index}
          value={`${menuItem.title ?? "menu-item"}-${index}`}
          icon={menuItem.icon}
          onClick={() => menuItemClickHandler(menuItem)}
          color={menuItem.menuItemColor || "gray.800"}
          disabled={menuItem.disabled}
          aria-label={menuItem.menuListLabel}
          data-testid={`ideas-card-menu-item-${menuItem.type ?? index}`}
        >
          {menuItem.title}
        </Menu.Item>
      );
    }
    return null;
  });

  // return the menu on vertical dots Icon click & pop alert dialog
  return (
    <Box data-testid="ideas-card-actions">
      {menuList.length === 1 && isEmpty(menuList[0]) ? (
        <FiMoreVertical size={24} />
      ) : (
        <Menu
          data-testid="ideas-card-menu"
          ariaLabelVerticalDotsIcon={`${translate("common.verticalDotsIcon")}`}
          menuAriaLabel={`${translate("common.actions")}`}
          menuButtonAriaLabel={`${translate("participantHeader.openMenu")}`}
        >
          {menuItems}
        </Menu>
      )}

      {currentAlertContent && (
        <AlertDialog
          open={isAlertOpen}
          onClose={closeAlert}
          data-testid="ideas-action-alert-dialog"
          {...currentAlertContent}
          onPrimaryClickHandler={() => {
            if (currentAlertContent.onPrimaryClickHandler) {
              currentAlertContent.onPrimaryClickHandler(currentData);
            }
            closeAlert();
          }}
          onSecondaryClickHandler={() => {
            if (currentAlertContent.onSecondaryClickHandler) {
              currentAlertContent.onSecondaryClickHandler(currentData);
            }
            closeAlert();
          }}
        />
      )}
    </Box>
  );
};

export default ActionList;
